import type {
  CampoDeTerreno,
  FotoDeTerreno,
  GpsDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import {
  conteoDeLaSeccion,
  momentoDeSincronizacion,
  pluralizar,
  tieneContenido,
} from "./registroDeTerreno";

/* ---------------------------------------------------------------------------
   Lo que la pestaña "Antecedentes generales" lee del registro de terreno: el
   punto de acceso, la portada y los audios/fotos/textos de las dos
   descripciones.

   Es lógica PURA y vive fuera del componente por la misma razón que el resto
   de `registroDeTerreno.ts`: la regla de "¿ya sincronizó?" o "¿es el mismo
   punto?" es la que decide qué texto ve la persona, y dentro del árbol un
   test no distingue una buena de una cercana.
--------------------------------------------------------------------------- */

/** La sección del registro de terreno que corresponde a esta pestaña. */
export const SECCION_DE_ANTECEDENTES = "antecedentes_generales";

/**
 * Los tres estados en que puede estar el registro de terreno MIENTRAS se edita
 * esta pestaña. Ninguno de los tres puede impedir que el formulario se dibuje
 * y se guarde.
 *
 * `no_disponible` es el informe que todavía no se aceptó (o el módulo apagado):
 * ahí el registro de terreno no existe y la pestaña se dibuja SIN ninguno de
 * sus paneles. Un `error` es otra cosa —el registro existe y no se pudo
 * leer— y sí se dice, con calma.
 */
export type EstadoDelTerreno =
  | { estado: "no_disponible" }
  | { estado: "cargando" }
  | { estado: "error" }
  | { estado: "ok"; registro: RegistroDeTerrenoDelInforme };

export function estadoDelTerreno({
  habilitado,
  error,
  registro,
}: {
  habilitado: boolean;
  error: boolean;
  registro: RegistroDeTerrenoDelInforme | undefined;
}): EstadoDelTerreno {
  if (!habilitado) return { estado: "no_disponible" };
  // Un dato ya cargado manda sobre un refetch fallido: el refresco al volver
  // a la ventana puede fallar sin que lo que se está viendo haya dejado de ser
  // cierto.
  if (registro) return { estado: "ok", registro };
  if (error) return { estado: "error" };

  return { estado: "cargando" };
}

export interface PuntoDeAcceso {
  latitud: number;
  longitud: number;
  precision_m: number | null;
  capturado_en: string | null;
  sincronizado_en: string | null;
  /** La sincronización en que llegó: lo que decide si se resalta. */
  sincronizacion_id?: string | null;
}

export interface VistaDeTerreno {
  /**
   * Si el encargado sincronizó ALGO alguna vez.
   *
   * ES LA REGLA QUE SEPARA DOS ESTADOS VACÍOS que la pantalla dice distinto:
   * "Aún sin sincronizar" (la App nunca mandó nada) y "el encargado no envió
   * registros para esta descripción" (mandó, pero no de esto). Sale de la
   * última sincronización del informe, y cae a "la sección trae algo" por si
   * el API alguna vez la mandara sin fecha.
   */
  sincronizado: boolean;
  seccion: SeccionDeTerreno | null;
  puntoDeAcceso: PuntoDeAcceso | null;
  /** La portada que mandó el campo, solo si trae el archivo. */
  portada: FotoDeTerreno | null;
  descripcion_ubicacion: CampoDeTerreno | null;
  descripcion: CampoDeTerreno | null;
  encargado: string | null;
}

export function vistaDeTerreno(registro: RegistroDeTerrenoDelInforme): VistaDeTerreno {
  const seccion = registro.secciones.find((s) => s.clave === SECCION_DE_ANTECEDENTES) ?? null;
  const campos = seccion?.campos ?? [];

  const gps: GpsDeTerreno | null = campos.find((c) => c.gps !== null)?.gps ?? null;
  const portada = campos.find((c) => c.portada !== null)?.portada ?? null;

  return {
    sincronizado:
      registro.ultima_sincronizacion !== null ||
      (seccion !== null && tieneContenido(conteoDeLaSeccion(seccion))),
    seccion,
    // Sin las dos mitades no hay punto: media coordenada es un dato roto.
    puntoDeAcceso:
      gps && gps.latitud !== null && gps.longitud !== null
        ? {
            latitud: gps.latitud,
            longitud: gps.longitud,
            precision_m: gps.precision_m,
            capturado_en: gps.capturado_en,
            sincronizado_en: gps.sincronizado_en,
            sincronizacion_id: gps.sincronizacion_id ?? null,
          }
        : null,
    portada: portada?.archivo ? portada : null,
    descripcion_ubicacion: campos.find((c) => c.clave === "descripcion_ubicacion") ?? null,
    descripcion: campos.find((c) => c.clave === "descripcion") ?? null,
    encargado: registro.encargado?.nombre ?? null,
  };
}

interface Coordenada {
  latitud: number;
  longitud: number;
}

/**
 * Seis decimales: lo mismo que redondea el selector al marcar un punto
 * (unos 11 cm). Es lo que hace que "Usar ubicación de terreno" deje de
 * ofrecerse una vez aplicado: el formulario guarda el punto redondeado, y
 * comparar a precisión completa lo daría por distinto para siempre.
 */
function aSeisDecimales(grados: number): string {
  return grados.toFixed(6);
}

export function mismaCoordenada(
  a: Coordenada | null | undefined,
  b: Coordenada | null | undefined,
): boolean {
  if (!a || !b) return false;

  return (
    aSeisDecimales(a.latitud) === aSeisDecimales(b.latitud) &&
    aSeisDecimales(a.longitud) === aSeisDecimales(b.longitud)
  );
}

/** El punto de terreno tal como lo guarda el formulario: a seis decimales. */
export function coordenadaDelPunto(punto: PuntoDeAcceso): Coordenada {
  return {
    latitud: Number(aSeisDecimales(punto.latitud)),
    longitud: Number(aSeisDecimales(punto.longitud)),
  };
}

const RADIO_DE_LA_TIERRA_M = 6_371_000;

/** Distancia sobre la esfera (haversine): alcanza para decidir, no se congela en nada. */
export function distanciaEnMetros(a: Coordenada, b: Coordenada): number {
  const rad = (grados: number) => (grados * Math.PI) / 180;
  const dLat = rad(b.latitud - a.latitud);
  const dLng = rad(b.longitud - a.longitud);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitud)) * Math.cos(rad(b.latitud)) * Math.sin(dLng / 2) ** 2;

  return 2 * RADIO_DE_LA_TIERRA_M * Math.asin(Math.sqrt(h));
}

export function textoDeDistancia(metros: number): string {
  return metros < 1000
    ? `${Math.round(metros)} m`
    : `${(metros / 1000).toFixed(1).replace(".", ",")} km`;
}

export interface ResumenDelCampo {
  audios: number;
  fotos: number;
  textos: number;
  /** La sincronización más reciente entre todo lo del campo, o `null` sin nada. */
  sincronizado_en: string | null;
}

export function resumenDelCampo(campo: CampoDeTerreno | null): ResumenDelCampo {
  if (!campo) return { audios: 0, fotos: 0, textos: 0, sincronizado_en: null };

  const momentos = [...campo.audios, ...campo.fotos, ...campo.textos]
    .map((registro) => registro.sincronizado_en)
    .filter((momento): momento is string => momento !== null)
    .sort();

  return {
    audios: campo.audios.length,
    fotos: campo.fotos.length,
    textos: campo.textos.length,
    sincronizado_en: momentos[momentos.length - 1] ?? null,
  };
}

/**
 * ¿Tiene el campo algo de lo que el servidor pueda redactar?
 *
 * Es el gemelo de `RedactorDesdeTerreno::materialDe()` (api): un texto escrito o
 * una transcripción LISTA. Las fotos no se leen y un audio pendiente o fallido
 * no tiene texto, así que "Redactar desde terreno" no se ofrece en esos casos
 * —el servidor respondería 422—.
 */
export function puedeRedactarDesdeTerreno(campo: CampoDeTerreno | null): boolean {
  if (!campo) return false;

  return (
    campo.textos.some((t) => t.texto.trim() !== "") ||
    campo.audios.some(
      (a) => a.transcripcion_estado === "lista" && (a.transcripcion ?? "").trim() !== "",
    )
  );
}

/** "2 audios · 1 foto · 1 texto · Sincronizado 05-07-2026 · 09:12", o "Sin registros". */
export function textoDelResumen(resumen: ResumenDelCampo): string {
  const partes = [
    resumen.audios > 0 && pluralizar(resumen.audios, "audio", "audios"),
    resumen.fotos > 0 && pluralizar(resumen.fotos, "foto", "fotos"),
    resumen.textos > 0 && pluralizar(resumen.textos, "texto", "textos"),
  ].filter(Boolean);

  if (partes.length === 0) return "Sin registros";

  if (resumen.sincronizado_en) {
    partes.push(`Sincronizado ${momentoDeSincronizacion(resumen.sincronizado_en)}`);
  }

  return partes.join(" · ");
}
